import { Check, ChevronDown, Search, X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { cn } from '@/utils/cn';

import { Popover, PopoverContent, PopoverTrigger } from './popover';
import type { PopoverPortalTarget } from './popover-portal';

/**
 * The single-select control. There is no native `<select>` anywhere in Trinity, and
 * every specialised picker — funds, members, currencies, countries — composes this
 * rather than re-implementing dropdown behaviour.
 *
 * Reference: documentation/architecture/widget-architecture.md §3.2.
 */

export type ListboxValue = string | number;

export interface ListboxOption<T extends ListboxValue = string> {
  value: T;
  label: string;
  /** A second line under the label — a phone number, a fund type, a balance. */
  description?: string;
  icon?: ReactNode;
  disabled?: boolean;
  /** Options sharing a group render under one heading, in first-seen order. */
  group?: string;
  /** Extra text the search matches on but does not display. */
  keywords?: string[];
}

export type ListboxSize = 'sm' | 'md' | 'lg' | 'kiosk';

export interface ListboxProps<T extends ListboxValue = string> {
  value: T | null;
  onChange: (value: T | null) => void;
  options: ListboxOption<T>[];
  /** Shown when nothing is selected. Comes from a locale file, never a literal. */
  placeholder?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Shown when a search matches nothing. */
  emptyMessage?: string;
  clearable?: boolean;
  clearLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  size?: ListboxSize;
  /**
   * Shrink the trigger to its content width. Used by composed controls like
   * `PhoneInput` where the dial-code button should not stretch.
   */
  compact?: boolean;
  id?: string;
  name?: string;
  /** Required inside any dialog. See dialog-dropdown-portal-architecture.md. */
  popoverPortal?: PopoverPortalTarget;
  renderValue?: (option: ListboxOption<T>) => ReactNode;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

const TRIGGER_SIZES: Record<ListboxSize, string> = {
  sm: 'h-8 px-2.5 text-body-sm',
  md: 'h-10 px-3 text-body',
  lg: 'h-12 px-4 text-card-title',
  kiosk: 'h-20 px-6 text-kiosk-body',
};

export const Listbox = <T extends ListboxValue = string>({
  value,
  onChange,
  options,
  placeholder,
  searchable = false,
  searchPlaceholder,
  emptyMessage,
  clearable = false,
  clearLabel,
  disabled = false,
  invalid = false,
  size = 'md',
  compact = false,
  id,
  name,
  popoverPortal = 'body',
  renderValue,
  className,
  ...aria
}: ListboxProps<T>) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const listId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const typeahead = useRef({ buffer: '', at: 0 });

  const selected = options.find((option) => option.value === value) ?? null;

  const visible = useMemo(() => {
    if (!searchable || !search.trim()) return options;
    const needle = search.trim().toLowerCase();
    return options.filter((option) =>
      [option.label, option.description, ...(option.keywords ?? [])]
        .filter(Boolean)
        .some((text) => text!.toLowerCase().includes(needle)),
    );
  }, [options, search, searchable]);

  const groups = useMemo(() => {
    const ordered: { name: string | null; options: ListboxOption<T>[] }[] = [];
    for (const option of visible) {
      const key = option.group ?? null;
      const existing = ordered.find((group) => group.name === key);
      if (existing) existing.options.push(option);
      else ordered.push({ name: key, options: [option] });
    }
    return ordered;
  }, [visible]);

  /** Flat, in render order, so arrow keys move the way the eye does across groups. */
  const flat = useMemo(() => groups.flatMap((group) => group.options), [groups]);

  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setSearch('');
      setActiveIndex(-1);
      return;
    }
    // Opening lands on the current selection rather than the top, so pressing
    // ArrowDown on a chosen option moves to the next one instead of jumping.
    const index = options.findIndex((option) => option.value === value);
    setActiveIndex(index >= 0 ? index : firstEnabled(options));
  };

  useEffect(() => {
    if (activeIndex < 0) return;
    listRef.current
      ?.querySelector(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const move = useCallback(
    (delta: number) => {
      setActiveIndex((current) => {
        if (flat.length === 0) return -1;
        let next = current;
        for (let step = 0; step < flat.length; step += 1) {
          next = (next + delta + flat.length) % flat.length;
          if (!flat[next]?.disabled) return next;
        }
        return current;
      });
    },
    [flat],
  );

  const commit = (option: ListboxOption<T> | undefined) => {
    if (!option || option.disabled) return;
    onChange(option.value);
    changeOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) changeOpen(true);
        else move(1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        if (!open) changeOpen(true);
        else move(-1);
        return;
      case 'Home':
        if (!open) return;
        event.preventDefault();
        setActiveIndex(firstEnabled(flat));
        return;
      case 'End':
        if (!open) return;
        event.preventDefault();
        setActiveIndex(lastEnabled(flat));
        return;
      case 'Enter':
        if (!open) {
          event.preventDefault();
          changeOpen(true);
          return;
        }
        event.preventDefault();
        commit(flat[activeIndex]);
        return;
      case ' ':
        // Space types into a search field; it only opens the menu when it cannot.
        if (searchable && open) return;
        event.preventDefault();
        if (!open) changeOpen(true);
        else commit(flat[activeIndex]);
        return;
      default:
        break;
    }

    // Type-ahead, for the unsearchable case. A long list without search is rare, but
    // "typing 'gh' to reach Ghana" is what a keyboard user expects from a dropdown.
    if (searchable || event.key.length !== 1 || event.metaKey || event.ctrlKey) return;

    const now = Date.now();
    const buffer =
      now - typeahead.current.at > 800
        ? event.key.toLowerCase()
        : typeahead.current.buffer + event.key.toLowerCase();
    typeahead.current = { buffer, at: now };

    const match = flat.findIndex(
      (option) => !option.disabled && option.label.toLowerCase().startsWith(buffer),
    );
    if (match < 0) return;

    if (!open) commit(flat[match]);
    else setActiveIndex(match);
  };

  const activeId = activeIndex >= 0 ? `${listId}-option-${activeIndex}` : undefined;
  let renderIndex = -1;

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : changeOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          name={name}
          role="combobox"
          disabled={disabled}
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={activeId}
          aria-invalid={invalid || undefined}
          onKeyDown={onKeyDown}
          className={cn(
            'flex items-center rounded-md border bg-surface text-left',
            'transition-colors',
            compact ? 'w-auto justify-start gap-1.5' : 'w-full justify-between gap-2',
            'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-fg-disabled',
            invalid ? 'border-danger' : 'border-border hover:border-border-strong',
            TRIGGER_SIZES[size],
            compact && 'px-2.5',
            className,
          )}
          {...aria}
        >
          <span
            className={cn(
              'flex min-w-0 items-center gap-2',
              !compact && 'flex-1',
            )}
          >
            {selected?.icon}
            <span
              className={cn(
                'truncate',
                selected ? 'text-fg' : 'text-fg-tertiary',
              )}
            >
              {selected ? (renderValue?.(selected) ?? selected.label) : placeholder}
            </span>
          </span>

          <span className="flex shrink-0 items-center gap-1">
            {clearable && selected && !disabled ? (
              <span
                role="button"
                tabIndex={-1}
                aria-label={clearLabel}
                onPointerDown={(event) => {
                  // Pointer-down rather than click: the trigger opens on click, so a
                  // clear handled later would open the menu on the way out.
                  event.preventDefault();
                  event.stopPropagation();
                  onChange(null);
                }}
                className="rounded p-0.5 text-fg-tertiary hover:bg-surface-sunken hover:text-fg"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            ) : null}
            <ChevronDown
              aria-hidden
              className={cn(
                'h-4 w-4 text-fg-tertiary transition-transform',
                open && 'rotate-180',
              )}
            />
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        popoverPortal={popoverPortal}
        // The panel is scaffolding around the listbox, not a dialog. Leaving Radix's
        // default role here would announce "dialog" to a screen reader between the
        // combobox and its options.
        role="presentation"
        className="flex max-h-[min(20rem,var(--radix-popover-content-available-height))] flex-col p-0"
        onOpenAutoFocus={(event) => {
          // Focus stays on the trigger unless there is a search field, so the
          // keyboard handling above stays in one place.
          if (!searchable) event.preventDefault();
        }}
      >
        {searchable ? (
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search aria-hidden className="h-4 w-4 shrink-0 text-fg-tertiary" />
            <input
              autoFocus
              value={search}
              placeholder={searchPlaceholder}
              onChange={(event) => {
                setSearch(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={onKeyDown}
              className="w-full bg-transparent text-body outline-none placeholder:text-fg-tertiary"
              aria-controls={listId}
              aria-activedescendant={activeId}
            />
          </div>
        ) : null}

        <div
          id={listId}
          ref={listRef}
          role="listbox"
          className="flex-1 overflow-y-auto overscroll-contain p-1"
        >
          {flat.length === 0 ? (
            <p className="px-3 py-6 text-center text-body-sm text-fg-secondary">
              {emptyMessage}
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.name ?? '_'} role="group" aria-label={group.name ?? undefined}>
                {group.name ? (
                  <p className="px-2 pt-2 pb-1 text-overline font-semibold tracking-wide text-fg-tertiary uppercase">
                    {group.name}
                  </p>
                ) : null}

                {group.options.map((option) => {
                  renderIndex += 1;
                  const index = renderIndex;
                  const isSelected = option.value === value;

                  return (
                    <div
                      key={String(option.value)}
                      id={`${listId}-option-${index}`}
                      data-index={index}
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={option.disabled || undefined}
                      onPointerEnter={() => !option.disabled && setActiveIndex(index)}
                      onClick={() => commit(option)}
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-body',
                        index === activeIndex && 'bg-surface-sunken',
                        option.disabled && 'cursor-not-allowed opacity-50',
                      )}
                    >
                      {option.icon}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-fg">{option.label}</span>
                        {option.description ? (
                          <span className="block truncate text-caption text-fg-secondary">
                            {option.description}
                          </span>
                        ) : null}
                      </span>
                      {isSelected ? (
                        <Check aria-hidden className="h-4 w-4 shrink-0 text-brand" />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const firstEnabled = <T extends ListboxValue>(options: ListboxOption<T>[]): number =>
  options.findIndex((option) => !option.disabled);

const lastEnabled = <T extends ListboxValue>(options: ListboxOption<T>[]): number => {
  for (let index = options.length - 1; index >= 0; index -= 1) {
    if (!options[index]?.disabled) return index;
  }
  return -1;
};
